// English fake text library — matches en.js structure with lorem-style placeholder text
import React from 'react';
import { profile as realProfile, experiences as realExperiences, education as realEducation } from './en';

const LOREM = 'lorem ipsum dolor sit amet consectetur adipiscing elit sed do eiusmod tempor incididunt ut labore et dolore magna aliqua ut enim ad minim veniam quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur excepteur sint occaecat cupidatat non proident sunt in culpa qui officia deserunt mollit anim id est laborum'.split(' ');

function fakeOfLength(n) {
  if (n === 0) return '';
  const words = [];
  let len = 0;
  while (len < n) {
    const w = LOREM[Math.floor(Math.random() * LOREM.length)];
    const wLen = words.length === 0 ? w.length : w.length + 1;
    if (len + wLen > n) break;
    words.push(w);
    len += wLen;
  }
  if (words.length === 0) return LOREM[0].slice(0, n);
  let s = words.join(' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// Structural keys preserved as-is (links → github.com)
const PRESERVE_KEYS = new Set(['type', 'id']);
const LINK_KEYS = new Set(['url', 'headerLink']);

function fakeString(str) {
  // Replace 4-digit years → 2077, keep "Present", dashes and commas unchanged, fake the rest
  const parts = [];
  const regex = /\b\d{4}\b|Present|[-–,， ]/g;
  let lastIdx = 0, match;
  while ((match = regex.exec(str)) !== null) {
    if (match.index > lastIdx) {
      parts.push(fakeOfLength(match.index - lastIdx));
    }
    const seg = match[0];
    if (/^\d{4}$/.test(seg)) {
      parts.push('2077');
    } else {
      parts.push(seg); // Present, –, -, ,, ， all kept as-is
    }
    lastIdx = match.index + match[0].length;
  }
  if (lastIdx < str.length) {
    parts.push(fakeOfLength(str.length - lastIdx));
  }
  return parts.join('');
}

// Clone a JSX child, preserving element structure (li/a/ul/p), replacing text & links
function fakeJSXChild(child) {
  if (typeof child === 'string') return fakeOfLength(child.length);
  if (typeof child === 'number') return fakeOfLength(String(child).length);
  if (child && child.$$typeof) {
    const props = {};
    if (child.props.className) props.className = child.props.className;
    if (child.props.href) props.href = 'https://github.com/ericpzh';
    // Recursively process children to preserve nested structure (ul > li > a, etc.)
    const children = child.props.children != null
      ? React.Children.map(child.props.children, fakeJSXChild)
      : null;
    return React.createElement(child.type, props, children);
  }
  return String(child);
}

function fakeDeep(obj) {
  if (typeof obj === 'string') return fakeString(obj);

  if (typeof obj === 'object' && obj !== null) {
    if (Array.isArray(obj)) return obj.map(fakeDeep);

    // React elements (JSX) → preserve structure, replace links with github.com, fake text
    if (obj.$$typeof) {
      const children = fakeDeep(obj.props.children);
      const props = {};
      if (obj.props.href) props.href = 'https://github.com/ericpzh';
      if (obj.props.className) props.className = obj.props.className;
      return React.createElement(obj.type, props, children);
    }

    // Plain object — recurse into values
    const result = {};
    for (const key of Object.keys(obj)) {
      if (PRESERVE_KEYS.has(key)) {
        result[key] = obj[key];
      } else if (LINK_KEYS.has(key)) {
        result[key] = obj[key] ? 'https://github.com/ericpzh' : obj[key];
      } else if (key === 'details' && obj[key] && obj[key].$$typeof) {
        // Expandable item details (React element): preserve child count & text length per child
        const originalChildren = React.Children.toArray(obj[key].props.children);
        const fakedChildren = originalChildren.map(fakeJSXChild);
        result[key] = React.createElement(React.Fragment, null, ...fakedChildren);
      } else {
        result[key] = fakeDeep(obj[key]);
      }
    }
    return result;
  }
  return obj;
}

export const profile = fakeDeep(realProfile);
export const experiences = fakeDeep(realExperiences);
export const education = fakeDeep(realEducation);
